import React, { useState, useEffect } from 'react';
import { useWalletBalance } from '../../hooks/useWalletBalance';
import './BalanceValidator.css';

/**
 * Balance Validator Component
 * Validates balance before trade execution and shows warnings/errors
 */
const BalanceValidator = ({ 
  walletId = null,
  tokenMint = null,
  amount = 0,
  includeFees = true,
  onValidationChange = null,
  showDetails = true,
  className = ''
}) => {
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  
  const {
    validateBalance,
    getTokenBalance,
    formatBalance,
    error: balanceError
  } = useWalletBalance(walletId);

  // Validate balance whenever inputs change
  useEffect(() => {
    const validateAmount = async () => {
      if (!amount || amount <= 0 || !tokenMint) {
        setValidationResult(null);
        if (onValidationChange) {
          onValidationChange({ valid: true, result: null });
        }
        return;
      }

      setIsValidating(true);
      
      try {
        const result = await validateBalance(tokenMint, parseFloat(amount), includeFees);
        setValidationResult(result);
        
        if (onValidationChange) {
          onValidationChange({ 
            valid: result.sufficient, 
            result: result 
          });
        }
      } catch (error) {
        console.error('Balance validation failed:', error);
        setValidationResult({
          sufficient: false,
          error: error.message
        });
        
        if (onValidationChange) {
          onValidationChange({ 
            valid: false, 
            result: { error: error.message } 
          });
        }
      } finally {
        setIsValidating(false);
      }
    };

    // Debounce validation
    const timeoutId = setTimeout(validateAmount, 300);
    return () => clearTimeout(timeoutId);
  }, [amount, tokenMint, includeFees, validateBalance, onValidationChange]);

  if (!amount || amount <= 0) {
    return null;
  }

  if (isValidating) {
    return (
      <div className={`balance-validator validating ${className}`}>
        <div className="validation-loading">
          <div className="loading-spinner"></div>
          <span>Validating balance...</span>
        </div>
      </div>
    );
  }

  if (balanceError || (validationResult && validationResult.error)) {
    return (
      <div className={`balance-validator error ${className}`}>
        <div className="validation-error">
          <span className="error-icon">⚠️</span>
          <span>Unable to validate balance</span>
        </div>
      </div>
    );
  }

  if (!validationResult) {
    return null;
  }

  const { sufficient, available, required, shortfall, currency, solBalance, solRequired, solSufficient } = validationResult;

  return (
    <div className={`balance-validator ${sufficient ? 'sufficient' : 'insufficient'} ${className}`}>
      {sufficient ? (
        <div className="validation-success">
          <span className="success-icon">✓</span>
          <span>Sufficient balance</span>
          {showDetails && (
            <div className="validation-details">
              Available: {available.toFixed(6)} {currency === 'SOL' ? 'SOL' : 'tokens'}
            </div>
          )}
        </div>
      ) : (
        <div className="validation-error">
          <span className="error-icon">⚠️</span>
          <span>Insufficient balance</span>
          {showDetails && (
            <div className="validation-details">
              <div className="balance-breakdown">
                <div className="balance-item">
                  <span>Required:</span>
                  <span>{required.toFixed(6)} {currency === 'SOL' ? 'SOL' : 'tokens'}</span>
                </div>
                <div className="balance-item">
                  <span>Available:</span>
                  <span>{available.toFixed(6)} {currency === 'SOL' ? 'SOL' : 'tokens'}</span>
                </div>
                <div className="balance-item shortage">
                  <span>Shortage:</span>
                  <span>{shortfall.toFixed(6)} {currency === 'SOL' ? 'SOL' : 'tokens'}</span>
                </div>
              </div>
              
              {currency === 'TOKEN' && !solSufficient && (
                <div className="sol-fee-warning">
                  <span className="warning-icon">⚠️</span>
                  <span>Also need {solRequired.toFixed(6)} SOL for transaction fees</span>
                  <span>(Available: {solBalance.toFixed(6)} SOL)</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BalanceValidator;